// Per-city logistical context surfaced on My Trip + the Plan flow. Bag
// storage spots, primary transit system, currency, language essentials,
// tipping, plug type, water safety, emergency number, eSIM tip, and a
// concierge prep checklist of stuff the user can ask the hotel to set up
// in advance.
//
// Portugal is the most detailed since that's our case study.

export const STOP_LOGISTICS = {
  Lisbon: {
    country: 'Portugal',
    currency: { code: 'EUR', symbol: '€', tip: 'Most places take card but small cafés and taxis sometimes only cash. Carry €30-50 cash.' },
    language: { code: 'pt', essentials: 'Obrigado/a (thanks) · Por favor (please) · Bom dia (good morning) · A conta (the bill)' },
    tipping: '10% at sit-down restaurants if service was good (not mandatory — service is rarely included). Round up taxis.',
    plug: 'Type F (Schuko, European 2-round-pin)',
    voltage: '230V / 50Hz',
    tapWater: 'Safe — Lisbon water is fine. Sparkling water at restaurants is "água com gás".',
    emergency: '112',
    visa: 'Schengen Area — US passport: 90 days visa-free in any 180-day period. Track your day count across all Schengen countries.',
    eSIM: 'Airalo Portugal 5GB ~$12 / Holafly unlimited 7-day €34. Vodafone has best urban coverage; MEO better in Algarve.',
    transit: {
      primary: 'Metro + AeroBus from airport. €1.50 single, €6.80 day pass. Buy a Viva Viagem card at the metro machine — €0.50 reusable card you load with credit.',
      taxi: 'Metered, ~€10 most city trips. Uber + Bolt work well, often cheaper than taxi.',
      tuktuk: 'Tourist-y but fun for Alfama hill — €25-40 for a 1h tour, set price before boarding.',
    },
    bagStorage: [
      { name: 'Bounce (multiple Alfama / Bairro Alto locations)', cost: '€6/day', url: 'https://usebounce.com' },
      { name: 'Radical Storage (40+ locations)', cost: '€5/day', url: 'https://radicalstorage.com' },
      { name: 'Stasher (4-5 city-center spots)', cost: '€6/day', url: 'https://stasher.com' },
    ],
    arrivalDayTip: 'Lisbon is hilly. Save the Alfama walk for tomorrow when you have energy. Tonight: light dinner near hotel + early sleep.',
    departureDayTip: 'Allow 90 min from hotel to LIS airport (taxi ~€12, AeroBus €4). LIS security can be slow — arrive 2.5h ahead for European departures.',
    conciergePrep: [
      'Pre-book a TukTuk Alfama tour for your second morning (better rate than walk-up)',
      'Reserve dinner at any of your fine-dining picks (Belcanto, Mesa de Frades)',
      'Arrange Sintra day-trip driver (€100-130 for the day)',
      'Ask if they\'ll hold late-arrived luggage (it will, free)',
    ],
    avoidThese: 'Rua Augusta restaurants (tourist traps) · Tram 28 at peak hours (sardine-tin packed) · Belém pastry imitators (only Pastéis de Belém is the original)',
    photoSpots: 'Miradouro da Senhora do Monte (sunset) · Tram 28 climbing the hill at Largo das Portas do Sol · Yellow buildings of Alfama from a rooftop bar',
  },
  Porto: {
    country: 'Portugal',
    currency: { code: 'EUR', symbol: '€', tip: 'Same as Lisbon — card accepted everywhere big, cash for small spots.' },
    language: { code: 'pt', essentials: 'Same Portuguese as Lisbon — locals are warmer + more patient with attempts to speak it' },
    tipping: 'Same as Lisbon — 10% appreciated at sit-down spots.',
    plug: 'Type F (same as Lisbon, no adapter swap)',
    voltage: '230V / 50Hz',
    tapWater: 'Safe.',
    emergency: '112',
    visa: 'Same Schengen status as Lisbon — day count continues.',
    eSIM: 'Same Airalo / Holafly plans work. Coverage along the Douro Valley can drop briefly — download offline maps.',
    transit: {
      primary: 'Metro (6 lines) + buses + the iconic Tram 1 along the Douro waterfront. Buy an Andante Azul card (€0.60 reusable) and load €5-10 of credit.',
      taxi: 'Cheap — €5-10 most city trips. Bolt + Uber both work, often €1-2 cheaper.',
      cable_car: 'Vila Nova de Gaia ⇄ Jardim do Morro funicular — €7 one-way, €10 round-trip. Great for a one-way ride down to the wine cellars.',
    },
    bagStorage: [
      { name: 'Bounce — multiple Ribeira spots', cost: '€6/day', url: 'https://usebounce.com' },
      { name: 'Porto São Bento station lockers', cost: '€4/locker' },
      { name: 'Radical Storage', cost: '€5/day', url: 'https://radicalstorage.com' },
    ],
    arrivalDayTip: 'After the train ride, you\'ll want to walk the riverfront. Take the cable car down to Vila Nova de Gaia + grab a port flight at one of the river-facing cellars (Sandeman has the best terrace at sunset).',
    departureDayTip: 'OPO airport is small but busy. Allow 75 min from city center (taxi €25 or Metro Line E ~€2.50 + 30 min). Lounges: TAP Premium for Star Alliance, plus Priority Pass at the ANA Lounge.',
    conciergePrep: [
      'Pre-book the Douro Valley day trip (best small-group operators sell out 1-2 weeks ahead in Sept harvest season)',
      'Reserve Yeatman or Antiqvvm if doing a Michelin night',
      'Get vintage steam train tickets for the Linha do Douro (Saturday only, sells out)',
      'Ask about Six Bridges cruise pickup — some hotels arrange shuttle',
    ],
    avoidThese: 'Rua das Flores after 11 PM (drunk crowd) · Port cellars that don\'t require booking (those are the bad ones) · Francesinha at hotel bars (go to Cafe Santiago instead)',
    photoSpots: 'Miradouro da Vitória (sunset over the river) · Dom Luís I Bridge upper deck at golden hour · Livraria Lello\'s staircase (arrive 9:30 AM for empty shots) · Ribeira waterfront from a Gaia rooftop',
  },
  Paris: {
    country: 'France',
    currency: { code: 'EUR', symbol: '€', tip: 'Card accepted everywhere. €20-30 cash for the boulangerie/market crowd.' },
    language: { code: 'fr', essentials: 'Bonjour (always say this first when entering a shop) · Merci · S\'il vous plaît · L\'addition' },
    tipping: 'Service is included by law — leaving a few coins (€1-2) is fine, 5-10% if exceptional. NEVER tip 20% American-style — it\'s considered odd.',
    plug: 'Type E (very similar to Type F, same adapter works)',
    voltage: '230V / 50Hz',
    tapWater: 'Safe — Parisians drink it. Carafe d\'eau at restaurants is free if you ask.',
    emergency: '112',
    visa: 'Schengen — same rules as Portugal.',
    eSIM: 'Airalo France 5GB ~$11. Orange has best coverage. Free Wi-Fi at most cafés (ask for the password — "code wifi").',
    transit: {
      primary: 'Metro is the fastest way around. €2.15 single, €17 for a 10-ticket carnet, or Navigo Easy card for €2 + loads of €1.99/ride. Buses + RER also useful for the périphérie.',
      taxi: 'Avoid central-Paris cabs at rush hour. Uber + Bolt fine. G7 is the legit taxi app.',
      bike: 'Vélib bike-share — €5/day for unlimited 30-min rides. Cycle on the Quai des Tuileries riverside path.',
    },
    bagStorage: [
      { name: 'Nannybag (everywhere)', cost: '€6/day', url: 'https://www.nannybag.com' },
      { name: 'Gare du Nord + Gare de Lyon lockers', cost: '€10/locker' },
      { name: 'Bounce', cost: '€6/day', url: 'https://usebounce.com' },
    ],
    arrivalDayTip: 'Don\'t do the Louvre or Versailles on arrival day — too much. Walk Île Saint-Louis at sunset, dinner at a bistro, save the big sights.',
    departureDayTip: 'CDG = 60 min by train (RER B €11) or 45 min by taxi (~€55 flat rate). Orly is closer (30 min). Allow 2.5-3h for international.',
    conciergePrep: [
      'Pre-book Louvre + Versailles timed-entry tickets',
      'Reserve Septime, L\'Astrance, or any 1-Michelin spot 30 days ahead',
      'Get Eurostar tickets if heading to London',
    ],
    avoidThese: 'Champs-Élysées restaurants (tourist prices) · Tour Eiffel restaurants (same) · Buying from street vendors near landmarks (counterfeit/scams)',
    photoSpots: 'Trocadéro for Eiffel · Pont Alexandre III at sunset · Le Bouillon Chartier (interior) · Sacré-Cœur dawn',
  },
  London: {
    country: 'United Kingdom',
    currency: { code: 'GBP', symbol: '£', tip: 'Card accepted everywhere — contactless tap is the norm for tube, buses, shops. Carry £30 cash for traditional pubs/markets.' },
    language: { code: 'en', essentials: 'You\'re fine — but "queue" not "line", "loo" not "bathroom", "pavement" not "sidewalk"' },
    tipping: '10-15% at sit-down restaurants if service charge isn\'t already added (check the bill). Round up taxis.',
    plug: 'Type G (the big chunky 3-pin) — NOT compatible with European Type F. Pack a UK adapter separately.',
    voltage: '230V / 50Hz',
    tapWater: 'Safe and good.',
    emergency: '999 or 112',
    visa: 'NOT Schengen — UK ETA required for US passport holders ($13, online, valid 2 years).',
    eSIM: 'Airalo UK 5GB ~$8. EE has best coverage. Most cafés have free Wi-Fi.',
    transit: {
      primary: 'Tube + bus + Overground + Elizabeth Line. Tap any contactless card or phone — daily caps automatically. £2.80 single tube, £8.10 daily cap zones 1-2.',
      taxi: 'Black cabs are an experience (knowledge-trained drivers). Uber + Bolt much cheaper for non-airport trips.',
      bike: 'Santander bike-share (Boris bikes) — £3/day for unlimited 30-min rides.',
    },
    bagStorage: [
      { name: 'Stasher (everywhere)', cost: '£6/day', url: 'https://stasher.com' },
      { name: 'St Pancras + King\'s Cross + Victoria lockers', cost: '£10/locker' },
      { name: 'Bounce', cost: '£6/day', url: 'https://usebounce.com' },
    ],
    arrivalDayTip: 'Tube to your hotel, pub dinner, early sleep. Save Tower of London / British Museum for tomorrow.',
    departureDayTip: 'LHR is 60 min via Piccadilly tube (£5) or 15 min via Heathrow Express (£25). Allow 3h for international. LGW is the budget hub — Gatwick Express £20, 30 min.',
    conciergePrep: [
      'Book West End theater tickets',
      'Reserve afternoon tea at Claridges / Fortnum & Mason (book weeks ahead)',
      'Pre-book Tower of London + Westminster Abbey timed entry',
    ],
    avoidThese: 'Leicester Square restaurants · Anything within 200m of Big Ben · The "Jack the Ripper" tours (just walk Whitechapel yourself)',
    photoSpots: 'Tower Bridge from the south bank at sunset · Borough Market golden hour · Primrose Hill sunset · Notting Hill pastel houses',
  },
};

export function getStopLogistics(city) {
  if (!city) return null;
  // Handle bundled cities — "Lisbon + Porto" returns Lisbon (the first stop)
  const primary = city.split(' + ')[0].trim();
  return STOP_LOGISTICS[primary] || STOP_LOGISTICS[city] || null;
}
